import { CssTransition } from '@mui/base/Transitions'
import { type CssTransitionProps } from '@mui/base/Transitions/CssTransition'
import {
  TransitionContext,
  useTransitionTrigger,
} from '@mui/base/useTransition'
import { clsx } from 'clsx'
import {
  forwardRef,
  type PropsWithChildren,
  useDeferredValue,
  useEffect,
} from 'react'

import styles from './DrawerTransition.module.css'

type DrawerTransitionProps = CssTransitionProps &
  PropsWithChildren<{
    in: boolean
    onEnter?: () => void
    onExited?: () => void
  }>

export const DrawerTransition = forwardRef<
  HTMLDivElement,
  DrawerTransitionProps
>(function ProductDrawerTransition(props, ref) {
  const { children, className, in: open, onEnter, onExited, ...rest } = props

  const { contextValue, hasExited } = useTransitionTrigger(open)

  /*
   * This is workaround for bug https://github.com/mui/material-ui/issues/40894.
   * Issue happens as `hasExited` changes too late and `onExited` is executed before animation starts.
   */
  const deferredHasExited = useDeferredValue(hasExited)

  useEffect(() => {
    if (contextValue.requestedEnter) {
      onEnter?.()
    }
  }, [contextValue.requestedEnter, onEnter])

  useEffect(() => {
    if (deferredHasExited) {
      onExited?.()
    }
  }, [deferredHasExited, onExited])

  return (
    <TransitionContext.Provider value={contextValue}>
      <CssTransition
        className={clsx(styles['drawer-transition'], className)}
        enterClassName={styles['drawer-transition_open']}
        ref={ref}
        {...rest}
      >
        {children}
      </CssTransition>
    </TransitionContext.Provider>
  )
})
